"use client";
import React, { useState } from "react";
import Image from "next/image";
import { toWebp } from "@/utils/common";

interface Props {
  data: any;
  isMobile: boolean;
}

const RequestProposalBusiness: React.FC<Props> = ({ data, isMobile }) => {
  let count = 0;
  const bgColors = ["[#E6F3FD]", "white", "[#F3F8E6]"];
  const [isOpenPremier, setIsOpenPremier] = useState(true);
  const [isOpenStandard, setIsOpenStandard] = useState(true);
  const url = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL;

  const getBgColor = (i: number) => {
    if (i === 0) count = 0;
    else if (count === 3) {
      count = 0;
      return bgColors[1];
    }
    const index = count;
    count++;
    return bgColors[index];
  };

  return (
    <section className="bg-white xl:pb-[90px] md:pb-[80px] pb-[56px]">
      <div className="container">
        {/* Title */}
        <div className="heading mb-[40px] xl:w-[70%] lg:w-[80%] mx-auto">
          <h2 className="text-center font-bold text-primary-text text-heading-1">
            {data?.title ?? "Business Capabilities"}
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[30%_40%_30%] xl:grid-cols-3">
          {/* Column 1 - Capability */}
          <div className="hidden lg:block bg-[#E6F3FD] sm:bg-transparent p-[16px_16px] lg:p-[16px_0px] xl:p-[24px_0px]">
            <div className="bg-white p-[24px_24px] xl:p-[39px_24px] flex flex-col justify-center min-h-[154px]">
              <h6 className="text-[20px] xl:text-[24px] font-[600] text-[#040405] leading-[26px] xl:leading-[31.2px] tracking-[1px]">
                Capability
              </h6>
            </div>
            <div className="content">
              {data?.capabilities?.map((item: any, i: number) => (
                <div
                  key={`capability-${i}`}
                  className={`p-[10px] flex flex-col justify-center bg-${getBgColor(i)} min-h-[133px]`}
                >
                  <p className="text-[18px] md:text-[20px] font-[600] text-[#6B6D75] leading-[25.2px] md:leading-[28px] tracking-[1px]">
                    {item.capability}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Column 2 - Premier Advantage */}
          <div className="bg-[linear-gradient(180deg,rgba(0,134,230,0.6)_0%,rgba(138,188,0,0.6)_100%),url('/images/bg-right.png')] bg-no-repeat bg-right bg-contain p-[16px_10px] xl:p-[24px_16px]">
            <div className="bg-white p-[10px] flex flex-col justify-center min-h-[154px] text-center">
              <Image
                src={toWebp(url + data?.premium_image?.data?.attributes.url)}
                alt="Premier Advantage"
                width={133}
                height={40}
                className="max-w-[133px] h-auto mx-auto"
              />
              <div className="relative flex justify-between items-center">
                <h6
                  className="w-full mt-[10px] p-[2px_8px] md:p-0 text-[20px] xl:text-[24px] font-[600] tracking-[1px] leading-[26px] xl:leading-[31.2px]"
                  style={{
                    WebkitTextFillColor: "transparent",
                    backgroundImage:
                      "linear-gradient(267.67deg, #0086e6 6.11%, #8abc00 90.91%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                  }}
                >
                  Premier Advantage
                </h6>
                {isMobile && (
                  <Image
                    src="/images/r-down-icon.svg"
                    alt="Toggle Premier"
                    width={20}
                    height={20}
                    onClick={() => setIsOpenPremier(!isOpenPremier)}
                    className={`absolute right-0 top-[30%] translate-y-[-50%] lg:hidden cursor-pointer transition-transform duration-300 ${isOpenPremier ? "rotate-0" : "-rotate-90"}`}
                  />
                )}
              </div>
            </div>
            {(isMobile ? isOpenPremier : true) && (
              <div className="content">
                {data?.capabilities?.map((item: any, i: number) => (
                  <div
                    key={`premier-${i}`}
                    className={`p-[10px] lg:p-[10px_12px] xl:p-[10px_12px] 2xl:p-[10px_35px] flex flex-col justify-center bg-${getBgColor(i)} min-h-[133px]`}
                  >
                    {isMobile && (
                      <p className="block lg:hidden mb-[10px] text-[18px] md:text-[20px] xl:text-[24px] text-[#6B6D75] font-[400] leading-[26px] md:leading-[28px] xl:leading-[31.2px] tracking-[1px] text-center">
                        {item.capability}
                      </p>
                    )}
                    <div
                      className="text-[18px] md:text-[20px] xl:text-[24px] font-[400] text-[#040405] leading-[26px] md:leading-[28px] xl:leading-[31.2px] tracking-[1px] text-center"
                      dangerouslySetInnerHTML={{
                        __html: item.premium_advantage ?? "<span></span>",
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Column 3 - Standard Offering */}
          <div className="bg-[#E6F3FD] lg:bg-transparent mt-[24px] lg:mt-0 p-[16px_16px] lg:p-[16px_0px] xl:p-[24px_0px]">
            <div className="bg-white p-[24px_10px] xl:p-[35px_10px] flex flex-col justify-center min-h-[154px] text-center">
              <Image
                src={toWebp(url + data?.standard_image?.data?.attributes.url)}
                alt="Standard Offering"
                width={31}
                height={31}
                className="max-w-[133px] h-[40px] mx-auto mb-[8px] block"
              />
              <div className="relative flex justify-between items-center">
                <h6 className="w-full text-[20px] xl:text-[24px] p-[2px_18px] md:p-0 font-[600] text-[#040405] leading-[26px] xl:leading-[31.2px] tracking-[1px]">
                  Standard Industry Offering
                </h6>
                {isMobile && (
                  <Image
                    src="/images/r-down-icon.svg"
                    alt="Toggle Standard"
                    width={20}
                    height={20}
                    onClick={() => setIsOpenStandard(!isOpenStandard)}
                    className={`absolute right-0 top-[30%] translate-y-[-50%] lg:hidden cursor-pointer transition-transform duration-300 ${isOpenStandard ? "rotate-0" : "-rotate-90"}`}
                  />
                )}
              </div>
            </div>

            {(isMobile ? isOpenStandard : true) && (
              <div className="content">
                {data?.capabilities?.map((item: any, i: number) => (
                  <div
                    key={`standard-${i}`}
                    className={`p-[10px] flex flex-col justify-center bg-${getBgColor(i)} min-h-[133px]`}
                  >
                    {isMobile && (
                      <p className="block lg:hidden mb-[10px] text-[18px] md:text-[20px] text-[#6B6D75] font-[600] leading-[28px] md:leading-[26px] tracking-[1px] text-center">
                        {item.capability}
                      </p>
                    )}
                    <div
                      className="text-[18px] md:text-[20px] font-[600] text-[#6B6D75] leading-[25.2px] md:leading-[28px] tracking-[1px] text-center"
                      dangerouslySetInnerHTML={{
                        __html:
                          item.standard_industry_offering ?? "<span></span>",
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequestProposalBusiness;
